import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast, Toaster } from "sonner";
import { KeyRound, ArrowLeft, ShieldCheck, Mail, Lock } from "lucide-react";

export default function AdminLogin() {
  const { login } = useAuth();
  const nav = useNavigate();
  
  // Views: "login" | "forgot" | "reset"
  const [view, setView] = useState("login");

  // Login form state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  // Password reset state
  const [resetEmail, setResetEmail] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetLoading, setResetLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) return toast.error("Please enter both email and password");
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome, ${u.name}`);
      if (u.role === "check_in") {
        nav("/admin/check-in");
      } else {
        nav("/admin");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  const handleRequestResetCode = async (e) => {
    e.preventDefault();
    if (!resetEmail) return toast.error("Please enter your staff email");
    setResetLoading(true);
    try {
      const res = await api.post("/auth/forgot-password", { email: resetEmail });
      toast.success(res.data?.message || "Reset code sent! Check your inbox.");
      if (res.data?.dev_code) {
        toast.info(`Verification code: ${res.data.dev_code}`, { duration: 15000 });
      }
      setView("reset");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send reset code");
    } finally {
      setResetLoading(false);
    }
  };

  const handleCompleteReset = async (e) => {
    e.preventDefault();
    if (!resetCode.trim()) return toast.error("Please enter the 6-digit verification code");
    if (!newPassword) return toast.error("Please enter a new password");
    if (newPassword.length < 8) return toast.error("Password must be at least 8 characters long");
    if (newPassword !== confirmPassword) return toast.error("Passwords do not match");

    setResetLoading(true);
    try {
      const res = await api.post("/auth/reset-password", {
        email: resetEmail,
        code: resetCode.trim(),
        new_password: newPassword,
      });
      toast.success(res.data?.message || "Password reset successfully!");
      // Automatically log in with new password
      try {
        const u = await login(resetEmail, newPassword);
        toast.success(`Signed in as ${u.name}`);
        if (u.role === "check_in") {
          nav("/admin/check-in");
        } else {
          nav("/admin");
        }
      } catch {
        // Fallback to login form if auto-login fails
        setEmail(resetEmail);
        setPassword("");
        setView("login");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed. Verify the code and try again.");
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <Toaster position="top-right" richColors />

      {/* LOGIN VIEW */}
      {view === "login" && (
        <form onSubmit={handleLogin} className="cgc-card p-8 w-full max-w-md space-y-5">
          <Link to="/" className="flex items-center gap-2 mb-2">
            <div className="w-9 h-9 rounded-lg bg-[color:var(--brand-terracotta)] flex items-center justify-center text-white font-display font-black">
              CG
            </div>
            <div>
              <div className="font-display font-bold">register | cgc india</div>
              <div className="text-[10px] font-mono text-stone-500 uppercase tracking-widest">Staff Portal</div>
            </div>
          </Link>

          <div>
            <h1 className="font-display text-2xl font-bold">Sign in</h1>
            <p className="text-sm text-stone-500">Staff & Event Handler access</p>
          </div>

          <div>
            <Label className="text-xs">Email</Label>
            <Input
              type="email"
              data-testid="admin-login-email"
              placeholder="staff@cgcollective.in"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="mt-1"
            />
          </div>

          <div>
            <div className="flex items-center justify-between">
              <Label className="text-xs">Password</Label>
              <button
                type="button"
                onClick={() => {
                  setResetEmail(email);
                  setView("forgot");
                }}
                className="text-xs text-[color:var(--brand-terracotta)] hover:underline"
              >
                Forgot password?
              </button>
            </div>
            <Input
              type="password"
              data-testid="admin-login-password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="mt-1"
            />
          </div>

          <Button data-testid="admin-login-submit" disabled={loading} className="w-full btn-brand h-11">
            {loading ? "Signing in…" : "Sign in"}
          </Button>

          <div className="text-center pt-2">
            <Link to="/" className="text-xs text-stone-500 hover:text-stone-800 dark:hover:text-stone-300">
              ← Return to public website
            </Link>
          </div>
        </form>
      )}

      {/* FORGOT PASSWORD: STEP 1 (REQUEST CODE) */}
      {view === "forgot" && (
        <form onSubmit={handleRequestResetCode} className="cgc-card p-8 w-full max-w-md space-y-5">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-9 h-9 rounded-lg bg-[color:var(--brand-terracotta)] flex items-center justify-center text-white">
              <KeyRound size={20} />
            </div>
            <div>
              <div className="font-display font-bold">Password Reset</div>
              <div className="text-[10px] font-mono text-stone-500 uppercase tracking-widest">Common Ground Staff</div>
            </div>
          </div>

          <div>
            <h1 className="font-display text-2xl font-bold">Reset Password</h1>
            <p className="text-sm text-stone-500 mt-1">
              Enter your registered staff email. We'll send you a secure 6-digit verification code.
            </p>
          </div>

          <div>
            <Label className="text-xs">Staff Email Address</Label>
            <Input
              type="email"
              placeholder="staff@cgcollective.in"
              value={resetEmail}
              onChange={(e) => setResetEmail(e.target.value)}
              required
              className="mt-1"
              autoFocus
            />
          </div>

          <Button disabled={resetLoading} className="w-full btn-brand h-11">
            {resetLoading ? "Sending Code…" : "Send Verification Code"}
          </Button>

          <div className="text-center pt-2">
            <button
              type="button"
              onClick={() => setView("login")}
              className="text-xs text-stone-500 hover:text-stone-800 dark:hover:text-stone-300 flex items-center justify-center gap-1 mx-auto"
            >
              <ArrowLeft size={12} /> Back to Sign In
            </button>
          </div>
        </form>
      )}

      {/* RESET PASSWORD: STEP 2 (VERIFY CODE & SET NEW PASSWORD) */}
      {view === "reset" && (
        <form onSubmit={handleCompleteReset} className="cgc-card p-8 w-full max-w-md space-y-5">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-9 h-9 rounded-lg bg-[color:var(--brand-terracotta)] flex items-center justify-center text-white">
              <ShieldCheck size={20} />
            </div>
            <div>
              <div className="font-display font-bold">Verify & Reset</div>
              <div className="text-[10px] font-mono text-stone-500 uppercase tracking-widest">{resetEmail}</div>
            </div>
          </div>

          <div>
            <h1 className="font-display text-2xl font-bold">Set New Password</h1>
            <p className="text-sm text-stone-500 mt-1">
              Enter the 6-digit code sent to your email and choose a strong password.
            </p>
          </div>

          <div>
            <Label className="text-xs">6-Digit Verification Code</Label>
            <Input
              type="text"
              maxLength={6}
              placeholder="123456"
              value={resetCode}
              onChange={(e) => setResetCode(e.target.value.replace(/\D/g, ""))}
              required
              className="mt-1 font-mono text-lg tracking-widest text-center"
              autoFocus
            />
          </div>

          <div>
            <Label className="text-xs">New Password (Min 8 chars)</Label>
            <Input
              type="password"
              placeholder="••••••••"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              className="mt-1"
            />
          </div>

          <div>
            <Label className="text-xs">Confirm New Password</Label>
            <Input
              type="password"
              placeholder="••••••••"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              className="mt-1"
            />
          </div>

          <Button disabled={resetLoading} className="w-full btn-brand h-11">
            {resetLoading ? "Updating Password…" : "Save New Password & Sign In"}
          </Button>

          <div className="flex items-center justify-between pt-2 text-xs">
            <button
              type="button"
              onClick={() => setView("forgot")}
              className="text-stone-500 hover:text-stone-800 dark:hover:text-stone-300"
            >
              Resend code
            </button>
            <button
              type="button"
              onClick={() => setView("login")}
              className="text-stone-500 hover:text-stone-800 dark:hover:text-stone-300"
            >
              Back to Sign in
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
